import { useState, useEffect } from 'react';
import axios from 'axios';
import { Send, Trash2, Bell, Smartphone, Link2, Users, ShieldAlert, CheckSquare, Square, Trophy, Megaphone, Tv, Monitor, Tablet, CalendarDays } from 'lucide-react';
import { EditorialPlanning } from './newsletter/EditorialPlanning';
import { toast } from 'sonner';
import { API } from './shared';

export function NotificationsTab({ theme }) {
  const light = theme === 'light';
  const [sub, setSub] = useState('push');
  const cardCls = light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5';
  const hintCls = light ? 'text-gray-400' : 'text-white/35';

  return (
    <div data-testid="notifications-tab">
      <h1 className={`text-2xl font-bold mb-1 ${light ? 'text-gray-900' : 'text-white'}`}>Notifications</h1>
      <p className={`text-xs mb-4 ${hintCls}`}>Envoyez des notifications ciblées par plateforme (TV, Web, Mobile, iPad), et consultez les alertes internes destinées aux admins du dashboard.</p>
      <div className={`inline-flex rounded-lg p-0.5 mb-6 ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
        {[['push', 'Envoi de notifications', Smartphone], ['planning', 'Planning automatique', CalendarDays], ['admin', 'Notifications admin', ShieldAlert]].map(([id, lbl, Icon]) => (
          <button key={id} data-testid={`notif-subtab-${id}`} onClick={() => setSub(id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-md transition-colors ${
              sub === id ? 'bg-[#E9641A] text-white' : light ? 'text-gray-500 hover:text-gray-800' : 'text-white/40 hover:text-white'}`}>
            <Icon size={13} /> {lbl}
          </button>
        ))}
      </div>
      {sub === 'push' && <PushSection light={light} cardCls={cardCls} hintCls={hintCls} />}
      {sub === 'planning' && <EditorialPlanning theme={theme} />}
      {sub === 'admin' && <AdminSection light={light} cardCls={cardCls} hintCls={hintCls} />}
    </div>
  );
}

const PLATFORMS = [
  ['tv', 'TV', Tv],
  ['web', 'Web', Monitor],
  ['mobile', 'Mobile', Smartphone],
  ['ipad', 'iPad', Tablet],
];
const PLATFORM_LABELS = { tv: 'TV', web: 'Web', mobile: 'Mobile', ipad: 'iPad' };

/* ─── Push notifications (mobile/tablet app users) ─── */
function PushSection({ light, cardCls, hintCls }) {
  const [notifs, setNotifs] = useState([]);
  const [contents, setContents] = useState([]);
  const [plans, setPlans] = useState([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [contentId, setContentId] = useState('');
  const [target, setTarget] = useState('all');
  const [selUsers, setSelUsers] = useState([]);
  const [userQuery, setUserQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [nominatif, setNominatif] = useState(false);
  useEffect(() => {
    if (target !== 'user' || userQuery.trim().length < 2) { setSuggestions([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/admin/subscribers`, { params: { search: userQuery.trim(), per_page: 8 }, withCredentials: true })
        .then(r => setSuggestions((r.data.rows || []).map(x => ({ email: x.email, name: x.name, has_account: x.has_account }))))
        .catch(() => setSuggestions([]));
    }, 350);
    return () => clearTimeout(t);
  }, [userQuery, target]);
  const [platforms, setPlatforms] = useState(['tv', 'web', 'mobile', 'ipad']);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const togglePlatform = (p) => setPlatforms(ps => ps.includes(p) ? ps.filter(x => x !== p) : [...ps, p]);

  const inputCls = light ? 'bg-white border border-gray-200 text-gray-800 placeholder-gray-400' : 'bg-white/5 border border-white/8 text-white placeholder-white/25';

  const load = () => axios.get(`${API}/admin/notifications`, { params: { kind: 'push' }, withCredentials: true }).then(r => setNotifs(r.data)).catch(() => {});
  useEffect(() => {
    load();
    axios.get(`${API}/contents`, { params: { include_drafts: false }, withCredentials: true }).then(r => setContents(r.data || [])).catch(() => {});
    axios.get(`${API}/plans`).then(r => setPlans(r.data || [])).catch(() => {});
  }, []);

  const send = async () => {
    if (!title.trim() || !message.trim() || sending || platforms.length === 0) return;
    if (target === 'user' && selUsers.length === 0) { toast.error('Sélectionnez au moins un abonné'); return; }
    setSending(true);
    setResult(null);
    try {
      const content = contents.find(c => c.id === contentId);
      const plan = plans.find(p => p.id === target);
      const { data } = await axios.post(`${API}/admin/notifications`, {
        title, message, content_id: contentId, content_title: content?.title || '',
        target,
        target_label: target === 'all' ? 'Tous les utilisateurs' : target === 'user' ? selUsers.map(u => u.name || u.email).join(', ') : (plan?.name || target),
        platforms, nominatif,
        emails: target === 'user' ? selUsers.map(u => u.email) : undefined,
      }, { withCredentials: true });
      setResult(data);
      setTitle(''); setMessage(''); setContentId(''); setTarget('all'); setSelUsers([]); setNominatif(false);
      load();
    } catch (err) {
      toast.error('Envoi échoué : ' + (err.response?.data?.detail || err.message));
    } finally { setSending(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette notification de l\'historique ?')) return;
    await axios.delete(`${API}/admin/notifications/${id}`, { withCredentials: true }).catch(() => {});
    load();
  };

  const [sendingDraft, setSendingDraft] = useState(null);
  const sendDraft = async (id) => {
    if (!window.confirm('Envoyer cette notification à tous les destinataires maintenant ?')) return;
    setSendingDraft(id);
    try {
      const { data } = await axios.post(`${API}/admin/notifications/${id}/send`, {}, { withCredentials: true });
      toast.success(data.devices_total > 0
        ? `Notification envoyée à ${data.devices_sent}/${data.devices_total} appareil(s)`
        : 'Notification envoyée — visible dans l\'app dès installation');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Envoi échoué'); }
    setSendingDraft(null);
  };

  return (
    <div data-testid="notif-push-section">
      <div className={`rounded-xl border p-5 mb-6 ${cardCls}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <label className={`block text-[10px] mb-1 uppercase tracking-wider ${hintCls}`}>Titre</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Ex : Nouveau film disponible !" data-testid="notif-title-input"
              className={`w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E9641A] ${inputCls}`} />
          </div>
          <div>
            <label className={`block text-[10px] mb-1 uppercase tracking-wider ${hintCls}`}><Link2 size={10} className="inline mr-1" />Lien vers un contenu (optionnel)</label>
            <select value={contentId} onChange={e => setContentId(e.target.value)} data-testid="notif-content-select"
              className={`w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E9641A] ${inputCls}`}>
              <option value="">Aucun — ouvre l'accueil de l'app</option>
              {contents.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div>
            <label className={`block text-[10px] mb-1 uppercase tracking-wider ${hintCls}`}>Message</label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} rows={2} placeholder="Ex : Découvrez The Royal Cat, disponible dès maintenant sur Maya+"
              data-testid="notif-message-input" className={`w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E9641A] resize-none ${inputCls}`} />
          </div>
          <div>
            <label className={`block text-[10px] mb-1 uppercase tracking-wider ${hintCls}`}><Users size={10} className="inline mr-1" />Cible</label>
            <select value={target} onChange={e => setTarget(e.target.value)} data-testid="notif-target-select"
              className={`w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E9641A] ${inputCls}`}>
              <option value="all">Tous les utilisateurs</option>
              {plans.map(p => <option key={p.id} value={p.id}>{p.name} ({p.price === 0 ? 'Gratuit' : `${p.price}€/${p.billing === 'annual' ? 'an' : 'mois'}`})</option>)}
              <option value="user">Abonnés spécifiques…</option>
            </select>
          </div>
        </div>
        {target === 'user' && (
          <div className="mb-4">
            <label className={`block text-[10px] mb-1.5 uppercase tracking-wider ${hintCls}`}>Destinataires (nom ou email)</label>
            <div className="relative">
              <input value={userQuery} onChange={e => setUserQuery(e.target.value)} placeholder="Rechercher un abonné…" data-testid="notif-user-search"
                className={`w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E9641A] ${inputCls}`} />
              {suggestions.length > 0 && (
                <div className={`absolute z-20 left-0 right-0 mt-1 rounded-lg border max-h-52 overflow-y-auto ${light ? 'bg-white border-gray-200 shadow-lg' : 'bg-[#241238] border-white/10'}`}>
                  {suggestions.map(s => (
                    <button key={s.email} type="button" data-testid={`notif-user-suggestion-${s.email}`}
                      onClick={() => { if (!selUsers.find(u => u.email === s.email)) setSelUsers(us => [...us, s]); setUserQuery(''); setSuggestions([]); }}
                      className={`w-full text-left px-3 py-2 text-xs ${light ? 'hover:bg-gray-50 text-gray-800' : 'hover:bg-white/5 text-white/80'}`}>
                      <b>{s.name || s.email}</b>{s.name ? <span className={`ml-2 ${hintCls}`}>{s.email}</span> : null}
                      {s.has_account === false && <span className="ml-2 text-[9px] text-amber-400">Sans compte Maya+ (ne recevra rien)</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {selUsers.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {selUsers.map(u => (
                  <span key={u.email} data-testid={`notif-user-chip-${u.email}`}
                    className="flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#E9641A]/15 text-[#E9641A] border border-[#E9641A]/30">
                    {u.name || u.email}
                    <button type="button" onClick={() => setSelUsers(us => us.filter(x => x.email !== u.email))} className="hover:text-white">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="mb-4">
          <label className={`flex items-center gap-2 cursor-pointer text-xs font-semibold ${light ? 'text-gray-700' : 'text-white/80'}`}>
            <input type="checkbox" checked={nominatif} onChange={e => setNominatif(e.target.checked)} data-testid="notif-nominatif-checkbox"
              className="accent-[#E9641A] w-3.5 h-3.5" />
            Nominatif — la notification commence par le prénom de l'abonné
          </label>
          {nominatif && <p className={`text-[10px] mt-1 ml-5 ${hintCls}`}>Ex : « <b>Hanae</b>, {message || 'nous avons ajouté un contenu qui va vous plaire.'} »</p>}
        </div>
        <div className="mb-4">
          <label className={`block text-[10px] mb-1.5 uppercase tracking-wider ${hintCls}`}>Plateformes de diffusion</label>
          <div className="flex flex-wrap gap-2">
            {PLATFORMS.map(([id, lbl, Icon]) => {
              const on = platforms.includes(id);
              return (
                <button key={id} type="button" onClick={() => togglePlatform(id)} data-testid={`notif-platform-${id}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold border transition-colors ${
                    on ? 'bg-[#E9641A] border-[#E9641A] text-white'
                       : light ? 'bg-white border-gray-200 text-gray-500 hover:border-gray-400' : 'bg-white/5 border-white/10 text-white/50 hover:border-white/30'}`}>
                  <Icon size={13} /> {lbl}
                </button>
              );
            })}
          </div>
          <p className={`text-[10px] mt-1.5 ${hintCls}`}>
            TV et Web : la notification apparaît dans la cloche. Mobile et iPad : envoi push sur l'app.
            {platforms.length === 0 && <span className="text-red-400 font-semibold ml-1">Sélectionnez au moins une plateforme.</span>}
          </p>
        </div>
        <div className="flex items-center justify-between">
          <p className={`text-[10px] ${hintCls}`}><Smartphone size={10} className="inline mr-1" />
            {platforms.includes('mobile') || platforms.includes('ipad')
              ? `Push envoyé aux appareils ${target === 'all' ? 'de tous les utilisateurs' : 'des abonnés du plan sélectionné'}`
              : 'Aucun push mobile — visible uniquement dans la cloche des plateformes cochées'}
          </p>
          <button onClick={send} disabled={sending || !title.trim() || !message.trim() || platforms.length === 0} data-testid="notif-send-btn"
            className="flex items-center gap-1.5 bg-[#E9641A] hover:bg-[#FE975E] text-white text-xs font-semibold px-5 py-2 rounded-lg transition-colors disabled:opacity-40">
            <Send size={13} /> {sending ? 'Envoi...' : 'Envoyer'}
          </button>
        </div>
        {result && (
          <div className={`mt-3 rounded-lg px-3 py-2 text-xs ${result.devices_total > 0 ? 'bg-green-500/10 text-green-500' : 'bg-amber-500/10 text-amber-500'}`} data-testid="notif-send-result">
            {result.devices_total > 0
              ? `Notification envoyée à ${result.devices_sent}/${result.devices_total} appareil(s).`
              : 'Notification enregistrée. Aucun appareil mobile enregistré pour le moment — elle sera visible dans l\'app dès que des utilisateurs l\'auront installée.'}
          </div>
        )}
      </div>

      <h3 className={`text-sm font-semibold mb-3 ${light ? 'text-gray-800' : 'text-white'}`}>Historique ({notifs.length})</h3>
      <div className="space-y-2">
        {notifs.map(n => (
          <div key={n.id} className={`flex items-start gap-3 rounded-xl border p-3.5 ${cardCls}`} data-testid={`notif-item-${n.id}`}>
            <span className="w-8 h-8 rounded-full bg-[#E9641A]/15 flex items-center justify-center flex-none mt-0.5"><Bell size={14} className="text-[#E9641A]" /></span>
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-semibold ${light ? 'text-gray-800' : 'text-white'}`}>
                {n.title}
                {n.draft && <span data-testid={`notif-draft-badge-${n.id}`} className="ml-2 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-500 border border-amber-500/30">Brouillon</span>}
              </p>
              <p className={`text-[11px] ${light ? 'text-gray-500' : 'text-white/50'}`}>{n.message}</p>
              <p className={`text-[10px] mt-1 ${hintCls}`}>
                {new Date(n.created_at).toLocaleString('fr-FR')}
                {n.content_title ? <span className="ml-2 text-[#E9641A]">→ {n.content_title}</span> : null}
                {n.target && n.target !== 'all' ? <span className="ml-2">· Cible : {n.target_label || n.target}</span> : null}
                {!n.draft && n.devices_total !== undefined && <span className="ml-2">· {n.devices_sent || 0}/{n.devices_total || 0} appareils</span>}
              </p>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {(n.platforms && n.platforms.length ? n.platforms : ['mobile', 'ipad']).map(p => (
                  <span key={p} className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${light ? 'bg-gray-100 text-gray-600' : 'bg-white/8 text-white/60'}`}>
                    {PLATFORM_LABELS[p] || p}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-none">
              {n.draft && (
                <button onClick={() => sendDraft(n.id)} disabled={sendingDraft === n.id} data-testid={`notif-send-draft-${n.id}`}
                  className="flex items-center gap-1 bg-[#E9641A] hover:bg-[#FE975E] text-white text-[10px] font-bold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
                  <Send size={11} /> {sendingDraft === n.id ? 'Envoi…' : 'Envoyer'}
                </button>
              )}
              <button onClick={() => remove(n.id)} className={`p-1.5 ${light ? 'text-gray-300 hover:text-red-500' : 'text-white/20 hover:text-red-400'}`} data-testid={`notif-delete-${n.id}`}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
        {notifs.length === 0 && <p className={`text-center text-sm py-8 ${hintCls}`}>Aucune notification envoyée pour le moment.</p>}
      </div>
    </div>
  );
}

/* ─── Admin notifications (dashboard alerts) with multi-select delete ─── */
function AdminSection({ light, cardCls, hintCls }) {
  const [notifs, setNotifs] = useState([]);
  const [selected, setSelected] = useState([]);
  const [deleting, setDeleting] = useState(false);

  const load = () => axios.get(`${API}/admin/notifications`, { params: { kind: 'admin' }, withCredentials: true })
    .then(r => { setNotifs(r.data); setSelected([]); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const allSelected = notifs.length > 0 && selected.length === notifs.length;
  const toggleAll = () => setSelected(allSelected ? [] : notifs.map(n => n.id));

  const deleteSelection = async () => {
    if (!selected.length || deleting) return;
    if (!window.confirm(`Supprimer ${selected.length} notification${selected.length > 1 ? 's' : ''} ?`)) return;
    setDeleting(true);
    try {
      const { data } = await axios.post(`${API}/admin/notifications/bulk-delete`, { ids: selected }, { withCredentials: true });
      toast.success(`${data.deleted} notification${data.deleted > 1 ? 's' : ''} supprimée${data.deleted > 1 ? 's' : ''}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setDeleting(false);
  };

  const iconFor = (type) => type === 'audience_record' ? <Trophy size={14} className="text-amber-400" />
    : type === 'ads_alert' ? <Megaphone size={14} className="text-red-400" />
    : <ShieldAlert size={14} className="text-[#E9641A]" />;

  return (
    <div data-testid="notif-admin-section">
      <p className={`text-xs mb-4 ${hintCls}`}>Alertes internes générées automatiquement pour les admins : records de connexions, campagnes publicitaires expirées, etc.</p>
      <div className="flex items-center justify-between mb-3">
        <button onClick={toggleAll} disabled={notifs.length === 0} data-testid="notif-admin-select-all"
          className={`flex items-center gap-1.5 text-xs font-semibold disabled:opacity-40 ${light ? 'text-gray-600 hover:text-gray-900' : 'text-white/60 hover:text-white'}`}>
          {allSelected ? <CheckSquare size={15} className="text-[#E9641A]" /> : <Square size={15} />}
          {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'} {selected.length > 0 && `(${selected.length})`}
        </button>
        <button onClick={deleteSelection} disabled={!selected.length || deleting} data-testid="notif-admin-delete-selection"
          className="flex items-center gap-1.5 bg-red-500/90 hover:bg-red-500 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-30">
          <Trash2 size={13} /> Supprimer la sélection {selected.length > 0 && `(${selected.length})`}
        </button>
      </div>
      <div className="space-y-2">
        {notifs.map(n => {
          const on = selected.includes(n.id);
          return (
            <div key={n.id} onClick={() => toggle(n.id)} data-testid={`notif-admin-item-${n.id}`}
              className={`flex items-start gap-3 rounded-xl border p-3.5 cursor-pointer transition-colors ${cardCls} ${on ? '!border-[#E9641A] ring-1 ring-[#E9641A]/40' : ''}`}>
              <span className="mt-1 flex-none" data-testid={`notif-admin-check-${n.id}`}>
                {on ? <CheckSquare size={16} className="text-[#E9641A]" /> : <Square size={16} className={light ? 'text-gray-300' : 'text-white/25'} />}
              </span>
              <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-none mt-0.5 ${light ? 'bg-gray-100' : 'bg-white/5'}`}>{iconFor(n.type)}</span>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-semibold ${light ? 'text-gray-800' : 'text-white'}`}>{n.title}</p>
                <p className={`text-[11px] ${light ? 'text-gray-500' : 'text-white/50'}`}>{n.message}</p>
                <p className={`text-[10px] mt-1 ${hintCls}`}>{new Date(n.created_at).toLocaleString('fr-FR')}</p>
              </div>
            </div>
          );
        })}
        {notifs.length === 0 && <p className={`text-center text-sm py-8 ${hintCls}`}>Aucune notification admin pour le moment.</p>}
      </div>
    </div>
  );
}
